import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import LimitMeter, { type Limit } from '../components/LimitMeter'
import type { Tree } from '../lib/types'

const ACCENT_CHOICES = ['gold', 'violet', 'teal', 'rose']
const ACCENT_DOT: Record<string, string> = {
  gold: 'bg-gold-400', violet: 'bg-rootv-400', teal: 'bg-teal-400', rose: 'bg-rose-400',
}

export default function Trees() {
  const [trees, setTrees] = useState<Tree[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [accent, setAccent] = useState('gold')
  const [busy, setBusy] = useState(false)
  const [createError, setCreateError] = useState('')
  // a limit of ONE has no "nearing" — it is empty or it is full — so this is a
  // plain statement of the fact rather than a progress bar (D-144)
  const [room, setRoom] = useState<{ limit: Limit; plan: string; purchases: boolean } | null>(null)
  useEffect(() => {
    api.getHeadroom()
      .then(h => { const l = h.limits.find(x => x.key === 'trees'); if (l) setRoom({ limit: l, plan: h.plan, purchases: h.purchases_enabled }) })
      .catch(() => {})
  }, [])

  const load = () => api.listTrees().then(setTrees).finally(() => setLoading(false))
  useEffect(() => { load() }, [])

  const create = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true); setCreateError('')
    try {
      const tree = await api.createTree(name.trim(), description.trim(), accent)
      setShowCreate(false); setName(''); setDescription('')
      setTrees(prev => [tree, ...prev])
    } catch (err) {
      const raw = err instanceof Error ? err.message : ''
      const clean = raw.replace(/^API \d+: /, '').replace(/[{}"]|error:/g, '').trim()
      // the server's 402 already says nothing was lost; appending it again printed
      // the same reassurance twice, 40 words apart, in a 60-word message
      setCreateError(clean || 'Could not create that constellation — please try again.')
    } finally { setBusy(false) }
  }

  const remove = async (t: Tree) => {
    if (!confirm(`Delete "${t.name}" and all ${t.person_count || 0} people in it? This cannot be undone.`)) return
    await api.deleteTree(t.id)
    setTrees(prev => prev.filter(x => x.id !== t.id))
  }

  if (loading) {
    return <div className="flex items-center justify-center py-32">
      <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-gold-400" />
    </div>
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-100">My Trees</h1>
          {/* No allowance count here. This header counts trees you can SEE (owned
              or collaborating), while the cap counts trees you OWN — so pairing them
              on one line told every seated editor "1 lineage under your care · 0 of 1
              constellation". Two numbers about "your trees" disagreeing across a
              middot. The meter below states the one that governs, once it matters. */}
          <p className="text-sm text-slate-500 mt-1">{trees.length} lineage{trees.length === 1 ? '' : 's'} under your care</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-gold">+ New Tree</button>
      </div>

      {/* above the grid, beside the button that triggers the refusal — under it you
          only found out by scrolling past your trees, or by clicking and failing */}
      {room && room.limit.limit >= 0 && room.limit.used >= room.limit.limit && (
        <LimitMeter limit={room.limit} plan={room.plan} purchasesEnabled={room.purchases} />
      )}

      {trees.length === 0 && (
        <div className="card-glass p-12 text-center">
          <div className="text-6xl mb-4">🌱</div>
          <p className="text-slate-400 mb-6">Every dynasty begins with a single name.</p>
          <button onClick={() => setShowCreate(true)} className="btn-gold">Plant Your First Tree</button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {trees.map(t => (
          <div key={t.id} className="card-glass p-5 group">
            <div className="flex items-start justify-between mb-2">
              <Link to={`/trees/${t.id}`} className="flex items-center gap-2.5 min-w-0">
                <span className={`w-3 h-3 rounded-full flex-shrink-0 ${ACCENT_DOT[t.accent] || ACCENT_DOT.gold}`} />
                <h3 className="font-display font-semibold text-lg text-slate-100 truncate group-hover:text-gold-300 transition-colors">{t.name}</h3>
              </Link>
              <button onClick={() => remove(t)} className="text-slate-600 hover:text-red-400 text-sm px-1" title="Delete tree">✕</button>
            </div>
            {t.description && <p className="text-xs text-slate-400 mb-3 line-clamp-2">{t.description}</p>}
            <div className="flex items-center justify-between">
              <div className="flex gap-4 text-xs text-slate-500">
                <span>✨ {t.person_count || 0} souls</span>
                <span>🔗 {t.relationship_count || 0} bonds</span>
              </div>
              <Link to={`/trees/${t.id}`} className="text-xs text-gold-400 hover:text-gold-300">Open constellation →</Link>
            </div>
          </div>
        ))}
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-void-950/80 backdrop-blur-sm flex items-center justify-center px-4" onClick={() => setShowCreate(false)}>
          <form onSubmit={create} onClick={e => e.stopPropagation()} className="card-glass p-7 w-full max-w-md agent-enter">
            <h2 className="font-display text-xl font-bold text-slate-100 mb-5">Plant a new tree</h2>
            {createError && (
              <div className="flex items-start gap-2.5 mb-4 px-3 py-2 rounded-xl border border-red-500/30 bg-red-500/[0.07] text-red-200 text-xs">
                <span className="text-sm">⚠</span><p className="flex-1">{createError}</p>
              </div>
            )}
            <input autoFocus required placeholder="Tree name — e.g. The Ashford Line"
              value={name} onChange={e => setName(e.target.value)} className="input-dark mb-3" />
            <textarea placeholder="What lineage does this hold? (optional)" rows={2}
              value={description} onChange={e => setDescription(e.target.value)} className="input-dark mb-4 resize-none" />
            <div className="flex items-center gap-3 mb-6">
              <span className="text-xs text-slate-500">Accent:</span>
              {ACCENT_CHOICES.map(a => (
                <button key={a} type="button" onClick={() => setAccent(a)}
                  className={`w-6 h-6 rounded-full ${ACCENT_DOT[a]} ${accent === a ? 'ring-2 ring-white ring-offset-2 ring-offset-void-900' : 'opacity-50'}`} />
              ))}
            </div>
            <div className="flex gap-3">
              <button type="submit" disabled={busy || !name.trim()} className="btn-gold flex-1">{busy ? 'Planting…' : 'Plant Tree'}</button>
              <button type="button" onClick={() => setShowCreate(false)} className="btn-ghost">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}
